<template src="./template.html"></template>
<script>
import Promise from 'bluebird'
import _filter from 'lodash/filter'
import _find from 'lodash/find'
import { mapState } from 'vuex'

import eventService from 'api/event'
import InventoryListItem from 'components/InventoryListItem'
import analyticsLib from 'lib/analytics'
import inventoryUtils from 'utils/inventoryUtils'
import daySettingService from 'api/daySetting'
import dateUtils from 'utils/dateUtils'
import AddAdmissionGroup from 'components/AddAdmissionGroup'
import SelectedInventory from 'components/SelectedInventory'
import * as mutationTypes from 'store/mutationTypes'
import store from 'store'
import inventoryService from 'api/inventory'
import TRACKING_EVENTS from 'constants/trackingEvents'

export default {
  name: 'inventory-list',
  components: {
    AddAdmissionGroup,
    InventoryListItem,
    SelectedInventory,
  },
  props: {
    eventId: {
      type: String,
    },
    sellingLink: {
      type: String,
    },
    inventoryId: {
      type: String,
    },
    inventoryType: {
      type: String,
    },
    hideTickets: {
      type: Boolean,
      default: false,
    },
    disableInlineCheckout: {
      type: Boolean,
      default: false,
    },
    disableGuestlist: {
      type: Boolean,
      default: false,
    },
    checkoutUrl: {
      type: String,
    },
    //THEMEING
    theme: {
      type: String,
      default: 'theme-light',
    },
    buttonBgColor: {
      type: String,
    },
    buttonTextColor: {
      type: String,
    },
    buttonText: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      selectedInventory: null,
      selectedInventoryQuantity: 1,
      showReservationRequest: false,
      showGuestlistRequest: false,
      //
      selectedSection: null,
      selectedSectionInventory: [],
      externalSellingLink: null,
    }
  },
  computed: {
    ...mapState({
      date: (state) => state.date.current,
      venue: (state) => state.venue.current,
      inventory: (state) => state.inventory.current,
      layoutSections: (state) => state.layoutSections.current,
      daySettings: (state) => state.daySettings.current,
    }),
    hasTickets() {
      return !!this.tickets.length
    },
    hasTables() {
      return !!this.tables.length
    },
    tickets() {
      const tickets =
        this.inventory && this.inventory.length
          ? _filter(this.inventory, {
              type: 'TICKET',
            })
          : []

      return tickets
    },
    tables() {
      const tables =
        this.inventory && this.inventory.length
          ? _filter(this.inventory, (item) => {
              return item.type === 'TABLE' || item.type === 'PACKAGE'
            })
          : []

      return tables
    },
    acceptingGuestlistExternally() {
      return this.daySettings && this.daySettings.acceptingGuestlistExternally
    },
    acceptingReservationRequests() {
      return this.daySettings && this.daySettings.acceptingReservationRequests
    },
  },
  watch: {
    date() {
      this.getInventory()
    },
  },
  methods: {
    getEvents() {
      return eventService
        .listByVenueId(this.venue.id, {
          dateStart: dateUtils.toUTCTimestampWithoutTime(this.date),
          dateEnd: dateUtils.toUTCTimestampWithoutTime(this.date),
          sort: 'date dateStart',
          limit: 1,
        })
        .then((result) => {
          if (!this.externalSellingLink && result.data && result.data.length) {
            const event = _find(result.data, (e) => !!e.externalSellingLink)

            this.externalSellingLink = (event || {}).externalSellingLink
          }
        })
    },
    getDaySettings() {
      return daySettingService.getByIdAndVenueId(dateUtils.toUTCTimestampWithoutTime(this.date), this.venue.id).then((result) => {
        this.$store.dispatch('setCurrentDaySettings', result.data)
      })
    },
    buyerTotal(inventory) {
      const buyerData = inventoryUtils.buyerTotal({
        inventoryType: inventory.type,
        price: inventory.price,
        taxPercent: inventory.tax,
        tipPercent: inventory.tip,
        ticketProcessingFee: this.venue.ticketProcessingFee,
        tableProcessingFee: this.venue.tableProcessingFee,
        fees: inventory.fees,
        depositAmount: inventory.depositAmount,
        paymentType: inventory.paymentType,
        userPaysProcessing: inventory.userPaysProcessing,
      })

      return buyerData
    },
    onInventoryMouseover(inventory) {
      this.$emit('inventoryMouseover', inventory)
    },
    onInventoryMouseleave(inventory) {
      this.$emit('inventoryMouseleave', inventory)
    },
    getInventory() {
      this.selectedInventory = null
      this.selectedSection = null
      this.loading = true

      const options = {
        type: this.inventoryType || undefined,
        dateStart: dateUtils.toUTCTimestampWithoutTime(this.date),
        dateEnd: dateUtils.toUTCTimestampWithoutTime(this.date),
      }

      return inventoryService
        .listByVenueId(this.venue.id, options)
        .then((results) => {
          return this.$store.dispatch('setCurrentInventory', results.data)
        })
        .then(() => {
          this.loading = false

          if (this.inventoryId) {
            const inventory = _find(this.inventory, {
              id: this.inventoryId,
            })

            if (inventory) {
              this.selectInventory(inventory)
            }
          }
        })
        .catch(() => {
          this.loading = false
        })
    },
    selectInventory(inventory) {
      if (!inventory || inventory.quantityLeft < 1) {
        return null
      }

      this.selectedInventory = inventory
      this.selectedSection = null
      this.selectedInventoryQuantity = this.selectedInventory.quantityMin || 1

      this.$emit('inventorySelected', {
        inventory: this.selectedInventory,
        inventoryQuantity: this.selectedInventoryQuantity,
      })

      return this.trackInventoryAdded()
    },
    selectSection(section) {
      const inventorySectionInventory = _filter(this.inventory, {
        venueLayoutSection: section.id,
      })

      this.selectedInventory = null
      this.selectedSection = section
      this.selectedSectionInventory = inventorySectionInventory
    },
    requestGuestlist() {
      this.showGuestlistRequest = true
    },
    requestReservation() {
      this.showReservationRequest = true
    },
    onAdmissionGroupCreated() {
      this.$emit('admissionGroupCreated')
    },
    onBookingFormSubmitted() {
      console.log('booking form submitted') //eslint-disable-line
    },
    onCheckout() {
      if (!this.$refs.selectedInventory.isValid()) {
        return
      }

      analyticsLib.trackEvent(TRACKING_EVENTS.AddedItemToCart, {
        venueId: this.venue.id,
        eventId: this.eventId,
        venueName: this.venue.name,
        type: this.selectedInventory.type,
        price: this.selectedInventory.price,
      })

      this.$store
        .dispatch('addCartItem', {
          inventoryId: this.selectedInventory.id,
          quantity: this.selectedInventoryQuantity,
        })
        .then(() => {
          return this.$store.dispatch('startCheckout')
        })
        .then(() => {
          return null
        })
    },
    onMutation(mutation) {
      if (mutation.type === mutationTypes.SET_CURRENT_DATE) {
        this.getDaySettings()
      }
    },
    //tracking
    trackViewedVenue() {
      return analyticsLib.trackEvent(TRACKING_EVENTS.ViewedVenue, {
        venueId: this.venue.id,
        venueName: this.venue.name,
      })
    },
    trackInventoryAdded() {
      return analyticsLib.trackEvent(TRACKING_EVENTS.AddedItemToCart, {
        venueId: this.venue.id,
        venueName: this.venue.name,
        inventoryId: this.selectedInventory.id,
        inventoryName: this.selectedInventory.name,
        inventoryType: this.selectedInventory.type,
        inventoryCannabisType: this.selectInventory.cannabisType,
      })
    },
  },
  mounted() {
    if (this.sellingLink) this.externalSellingLink = this.sellingLink

    this.getInventory()

    this.storeUnsubscribe = store.subscribe(this.onMutation)

    Promise.all([this.getDaySettings(), this.getEvents(), this.trackViewedVenue()])
  },
  updated() {
    this.$emit('updated')
  },
  destroyed() {
    this.storeUnsubscribe()
  },
}
</script>



// WEBPACK FOOTER //
// src/components/InventoryList/index.vue